import { isEmpty } from 'radash';
import { useController } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Input } from '@/components/core/Input';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useLengthMessage } from '../hooks/useLengthMessage';
import { usePlaceholder } from '../hooks/usePlaceholder';
import { useValidationsStore,Validation } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';

interface StringComponentProps extends EditorComponent {
    // String specific props go here
    isDimensional?: boolean;
    className?: string;
}

const StringComponent = ({
    attribute,
    direction = 'horizontal',
    fieldPath = attribute.name,
    gridMode = false,
    hasErrors: parentHasErrors = false,
    hasWarnings: parentHasWarnings = false,
    hidden = false,
    readonly = false,
    hideLabel = false,
    isKeyAttribute = false,
    isDimensional = false,
    beforeAttributeControl,
    className,
    handleUrlChange,
    labelClassName,
    onBlur: parentOnBlur
}: StringComponentProps) => {

    const focusRef = useFocusOnAttribute<HTMLInputElement>(attribute);

    const placeholder = usePlaceholder(attribute, readonly);

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = parentHasErrors || !isEmpty(validationErrors);
    const hasWarnings = parentHasWarnings || !isEmpty(validationWarnings);

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const { field } = useController({
        name: fieldPath,
    });

    const currentValue = field.value ?? '';

    const lengthMessage = useLengthMessage(currentValue?.length, attribute.params?.length);

    const setRef = (e: HTMLInputElement | null) => {
        if (e) {
            (focusRef as React.MutableRefObject<HTMLInputElement | null>).current = e;
        }
        field.ref(e);
    };

    /**
     * Prevents text selection when the input field is focused (e.g., during validation).
     *
     * When validation runs and focuses a field with errors, browsers typically select all text by default.
     * This handler moves the cursor to the end of the text instead, preventing accidental text deletion
     * when the user starts typing after validation.
     *
     * Uses requestAnimationFrame to ensure this runs after the browser's default selection behavior.
     */
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        requestAnimationFrame(() => {
            const input = e.target;
            if (input && document.activeElement === input) {
                const length = input.value.length;
                input.setSelectionRange(length, length);
            }
        });
    };

    const handleBlur = () => {
        field.onBlur();
        if (parentOnBlur) {
            parentOnBlur();
        }
    };

    if (hidden) {
        return null;
    }

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={gridMode}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={!gridMode && !readonly && !isDimensional}
            labelClassName={labelClassName}
        >
            <div className="tw-flex tw-flex-row">
                <Input
                    id={fieldPath}
                    borderless={gridMode}
                    placeholder={placeholder}
                    onClick={handleUrlChange}
                    {...field}
                    value={currentValue}
                    disabled={readonly}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    ref={setRef}
                    gridMode={gridMode}
                    className={`w-full tw-h-[36px] ${hasErrors ? 'error-input' : hasWarnings ? 'warning-input' : ''} tw-bg-white ${className}`}  />

                { (attribute.params?.uiShowLength && !readonly) &&
                <div className={`input lg-font-dynamic tw-flex tw-items-center tw-bg-gray-100 tw-whitespace-nowrap tw-text-center tw-overflow-hidden
                    ${!gridMode ? 'tw-px-3 tw-h-[36px]' : 'tw-px-2'} ${lengthMessage.length < 3 ? 'tw-w-16' : 'tw-w-20'}`}>
                    {lengthMessage}
                </div>
                }
            </div>
        </Layout>
    );
};

export { StringComponent };
